import React, { useEffect, useState, useCallback } from 'react';
import {
  getHealth, getDailyPnl, getPositions, getTrades, getSignals,
  getTradingStatus, postKillSwitch, toggleAutoExecute, postFlatten, getTradovateHealth,
} from '../api';
import { Activity, Clock, Calendar, Inbox } from 'lucide-react';
import LivePositionPanel from './LivePositionPanel';
import { formatTradeTime } from '../utils/time';
import { pnlColorClass } from '../utils/pnl';
import { PageHeader } from './ui/PageHeader';
import { SectionHeader } from './ui/SectionHeader';
import { KpiCard } from './ui/KpiCard';
import { EmptyState } from './ui/EmptyState';
import { InstrumentCell } from './ui/InstrumentCell';

type MacroInfo = { label: string; deltaMs: number | null };
type SessionInfo = { label: string };

const ET_TZ = 'America/New_York';

function getEtParts(now: Date) {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: ET_TZ,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    weekday: 'short',
  });
  const parts = fmt.formatToParts(now);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const weekday = parts.find((p) => p.type === 'weekday')?.value ?? '';
  return { hour: get('hour'), minute: get('minute'), second: get('second'), weekday };
}

// NQ/MNQ futures: Sun 18:00 ET open, Fri 17:00 ET close, daily 17:00-18:00 maintenance
function isFuturesOpen(hour: number, minute: number, weekday: string): boolean {
  if (weekday === 'Sat') return false;
  if (weekday === 'Sun') return hour >= 18;
  if (weekday === 'Fri' && hour >= 17) return false;
  if (hour === 17) return false; // daily maintenance
  return true;
}

function getNextMacro(now: Date): MacroInfo {
  const { hour, minute, second, weekday } = getEtParts(now);
  if (!isFuturesOpen(hour, minute, weekday)) return { label: 'Market closed', deltaMs: null };

  const targets = [20, 50];
  let bestDelta = Infinity;
  let bestHour = hour;
  let bestMin = 0;
  // Search up to 24 hours, stopping at maintenance/close windows
  for (let h = hour; h < hour + 24; h++) {
    const hMod = h % 24;
    // Stop at daily maintenance or Friday close
    if (hMod === 17) break;
    for (const t of targets) {
      if (h === hour && t <= minute) continue;
      const delta = (h - hour) * 3600 + (t - minute) * 60 - second;
      if (delta > 0 && delta < bestDelta) {
        bestDelta = delta;
        bestHour = hMod;
        bestMin = t;
      }
    }
  }
  if (bestDelta === Infinity) return { label: 'Market closed', deltaMs: null };
  const mins = Math.floor(bestDelta / 60);
  const secs = bestDelta % 60;
  const tHour = ((bestHour + 11) % 12) + 1;
  const ampm = bestHour < 12 ? 'AM' : 'PM';
  const target = `${tHour}:${String(bestMin).padStart(2, '0')} ${ampm} ET`;
  const label = mins > 0 ? `${mins}m ${secs}s (${target})` : `${secs}s (${target})`;
  return { label, deltaMs: bestDelta * 1000 };
}

// Fix 3: when market closed, surface the next macro window in human time.
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
function getNextMacroWhenClosed(now: Date): string {
  const { hour, weekday } = getEtParts(now);
  const wdIdx = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(weekday);
  // Maintenance window Mon-Thu 17:00-18:00
  if (wdIdx >= 1 && wdIdx <= 4 && hour === 17) {
    return `Maintenance window - market reopens 6:00 PM ET`;
  }
  // Friday after 17:00 or Saturday: reopens Sunday 18:00
  if ((wdIdx === 5 && hour >= 17) || wdIdx === 6) {
    return `Market closed - reopens Sunday 6:00 PM ET`;
  }
  // Sunday before 18:00
  if (wdIdx === 0 && hour < 18) {
    return `Market closed - reopens today 6:00 PM ET`;
  }
  // Open but past last macro of session
  return `Market closed - next macro resumes after maintenance`;
}

function getCurrentSession(now: Date): SessionInfo {
  const { hour, minute, weekday } = getEtParts(now);
  // NQ/MNQ true futures hours
  if (weekday === 'Sat') return { label: 'Market Closed' };
  if (weekday === 'Sun' && hour < 18) return { label: 'Market Closed' };
  if (weekday === 'Fri' && hour >= 17) return { label: 'Market Closed' };
  if (hour === 17) return { label: 'Maintenance' };

  if (hour >= 18 || hour < 2) return { label: 'Asia' };
  if (hour >= 2 && hour < 7) return { label: 'London' };
  if (hour >= 7 && (hour < 9 || (hour === 9 && minute < 30))) return { label: 'Pre-Market' };
  if ((hour === 9 && minute >= 30) || (hour > 9 && hour < 12)) return { label: 'AM' };
  if (hour >= 12 && hour < 13) return { label: 'Lunch' };
  if (hour >= 13 && hour < 16) return { label: 'PM' };
  if (hour >= 16 && hour < 17) return { label: 'After Hours' };
  return { label: 'Market Closed' };
}

function toneFor(value: number | null | undefined): 'positive' | 'negative' | 'neutral' {
  if (value === null || value === undefined) return 'neutral';
  if (value > 0) return 'positive';
  if (value < 0) return 'negative';
  return 'neutral';
}

export default function Dashboard() {
  const [health, setHealth] = useState<any>(null);
  const [pnl, setPnl] = useState<any[]>([]);
  const [positions, setPositions] = useState<any[]>([]);
  const [trades, setTrades] = useState<any[]>([]);
  const [signals, setSignals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState<Date>(new Date());

  useEffect(() => {
    const load = async () => {
      try {
        const [h, p, pos, t, s, tv] = await Promise.all([
          getHealth(), getDailyPnl(), getPositions(), getTrades(), getSignals(), getTradovateHealth(),
        ]);
        // Dashboard-accuracy fix (2026-07-15): was preferring the broker's
        // session-cumulative realized_pnl (resets on Tradovate's own session
        // boundary, not ET midnight), which could disagree with every other
        // "Today P&L" on the site. Standardize on today_pnl_db (DB-truth,
        // closed trades only, ET midnight cutoff) -- same figure /health's
        // tradovate_realized_pnl already computed, now also on this payload.
        setHealth({ ...h.data, tradovate_realized_pnl: tv.data?.today_pnl_db ?? h.data?.tradovate_realized_pnl });
        setPnl(p.data);
        setPositions(pos.data);
        setTrades(t.data);
        setSignals(s.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(tick);
  }, []);

  const todayPnl: number | null = health?.tradovate_realized_pnl ?? null;
  const todayET = new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" }); // YYYY-MM-DD in ET
  const todayRow = pnl[0]?.date === todayET ? pnl[0] : null;
  const todayTrades = todayRow?.trades_count || 0;
  const winRate = todayRow?.win_rate || 0;
  const accountBalance = health?.account_balance ?? health?.account_starting_balance ?? 0;
  const startingBalance = health?.account_starting_balance ?? 0;

  const lastTrades = [...trades]
    .filter(t => t.status === 'closed' && t.close_time)
    .sort((a, b) => new Date(b.close_time).getTime() - new Date(a.close_time).getTime())
    .slice(0, 3);

  // All-time cumulative: includes orphan P&L in running total
  const closedAll = trades.filter(t => t.status === 'closed' && t.close_time);
  const allTimeNet = closedAll.reduce((sum: number, t: any) => sum + (t.net_pnl ?? t.realized_pnl ?? t.pnl ?? 0), 0);
  const orphanCount = trades.filter(t => t.status === 'closed' && t.signal_class === 'ORPHAN').length;
  // Dashboard-accuracy fix (2026-07-15): "since May 19" was a hardcoded
  // string that silently went stale as soon as it was written. Derive the
  // earliest closed-trade date from the actual data instead.
  const earliestCloseDate = closedAll.length > 0
    ? new Date(Math.min(...closedAll.map(t => new Date(t.close_time).getTime())))
        .toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'America/New_York' })
    : null;

  // Today's signal feed (most recent first, last 24h)
  const todaySignalFeed = (() => {
    const cutoff = Date.now() - 24 * 3600 * 1000;
    return [...signals]
      .filter(s => s.created_at && new Date(s.created_at).getTime() >= cutoff)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  })();

  const macro = getNextMacro(now);
  const session = getCurrentSession(now);
  const marketClosed = macro.deltaMs === null;

  if (loading) return <DashboardLoading />;

  const positionStatus: 'FLAT' | 'LONG' | 'SHORT' | 'MIXED' = (() => {
    if (!positions || positions.length === 0) return 'FLAT';
    const dirs = new Set(positions.map((p: any) => p.direction));
    if (dirs.size > 1) return 'MIXED';
    return positions[0].direction === 'long' ? 'LONG' : 'SHORT';
  })();

  return (
    <div>
      <PageHeader
        title="Dashboard"
        description="Live system status. Account balance, open position, today's P&L, and signal pipeline at a glance."
      />

      <TradingControlsBar positionStatus={positionStatus} />

      <div className="space-y-6">
        <LivePositionPanel />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard
            label="Today P&L"
            value={todayPnl === null ? '--' : `$${todayPnl.toFixed(2)}`}
            tone={toneFor(todayPnl)}
            sub={todayPnl === null ? 'Tradovate offline' : undefined}
          />
          <KpiCard label="Trades Today" value={todayTrades} />
          <KpiCard label="Win Rate" value={`${winRate.toFixed(1)}%`} />
          <KpiCard
            label="Account Balance"
            value={`$${accountBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            tone={accountBalance >= startingBalance ? 'positive' : 'negative'}
            sub={`Start: $${startingBalance.toLocaleString()}`}
          />
        </div>

        {/* Signals feed + Equity KPI — same row */}
        <div className="grid grid-cols-3 gap-3">
          {/* Today's signals feed */}
          <div className="col-span-2 bg-gray-900 border border-gray-800 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 uppercase tracking-wider">Today's Signals</span>
              <span className="text-[10px] text-gray-600">{todaySignalFeed.length} fired</span>
            </div>
            {todaySignalFeed.length === 0 ? (
              <div className="text-xs text-gray-600 py-3">No signals today</div>
            ) : (
              <div className="space-y-1">
                {todaySignalFeed.slice(0, 8).map((s: any, i: number) => {
                  const executed = s.tradovate_executed === true;
                  const skipped = s.status === 'rejected' || s.status === 'skipped';
                  const t = new Date(s.created_at);
                  const timeStr = t.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York' });
                  const cls = (s.signal_class || 'SIGNAL').replace('_', ' ');
                  const dir = (s.direction || '').toUpperCase();
                  const score = s.quality_score ?? '--';
                  const reason = s.tradovate_reason ? String(s.tradovate_reason).split(':')[0] : '';
                  return (
                    <div key={i} className="flex items-center gap-2 py-0.5">
                      <span className="text-[10px] text-gray-600 w-14 flex-shrink-0 font-mono">{timeStr}</span>
                      <span className={`text-[10px] font-bold flex-shrink-0 w-5 ${dir === 'LONG' ? 'text-green-400' : 'text-red-400'}`}>
                        {dir === 'LONG' ? '▲' : '▼'}
                      </span>
                      <span className="text-[10px] text-gray-400 flex-shrink-0 w-20 truncate">{cls}</span>
                      <span className="text-[10px] font-mono text-gray-500 flex-shrink-0 w-8">{score}</span>
                      <span className={`text-[10px] font-semibold flex-shrink-0 ml-auto ${executed ? 'text-blue-400' : skipped ? 'text-gray-600' : 'text-yellow-500'}`}>
                        {executed ? '✓ EXEC' : skipped ? (reason || 'SKIP') : s.status?.toUpperCase() || '--'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Equity KPI */}
          <KpiCard
            label="Cumulative P&L"
            value={closedAll.length === 0 ? '--' : `${allTimeNet >= 0 ? '+' : ''}$${allTimeNet.toFixed(2)}`}
            tone={toneFor(allTimeNet)}
            sub={closedAll.length === 0 ? 'No closed trades' : `${closedAll.length} trades since ${earliestCloseDate}${orphanCount > 0 ? ' (incl. ' + orphanCount + ' orphan)' : ''}`}
          />

        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <div className="bg-gray-900 rounded-lg border border-gray-800 p-5">
            <SectionHeader title="Open Positions" />
            {positions.length === 0 ? (
              <EmptyState
                icon={<Inbox size={28} />}
                message="No open positions"
                sub="Position cards will appear here when a trade is active."
              />
            ) : (
              <div className="space-y-2">
                {positions.map((p: any) => (
                  <div key={p.id} className="flex items-center justify-between bg-gray-800 rounded p-2.5">
                    <div className="flex items-center gap-2">
                      <DirectionBadge dir={p.direction} />
                      <InstrumentCell symbol={p.instrument} className="font-medium" />
                      <span className="text-gray-500 text-xs">{p.quantity} contracts</span>
                      {p.exit_strategy && (
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${p.exit_strategy === 'simple' ? 'bg-blue-900 text-blue-300' : 'bg-purple-900 text-purple-300'}`}>
                          {p.exit_strategy}
                        </span>
                      )}
                    </div>
                    <span className={`font-mono text-sm ${pnlColorClass(p.unrealized_pnl)}`}>
                      ${(p.unrealized_pnl ?? 0).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-gray-900 rounded-lg border border-gray-800 p-5">
            <SectionHeader title="Last 3 Trades" />
            {lastTrades.length === 0 ? (
              <EmptyState
                icon={<Inbox size={28} />}
                message="No trades yet"
                sub="Closed trade history will populate here."
              />
            ) : (
              <div className="space-y-2">
                {lastTrades.map((t: any) => {
                  const pnlVal = t.net_pnl ?? t.realized_pnl ?? t.pnl ?? 0;
                  return (
                    <div key={t.id} className="flex items-center justify-between bg-gray-800 rounded p-2.5">
                      <div className="flex items-center gap-3">
                        <DirectionBadge dir={t.direction} />
                        <InstrumentCell symbol={t.instrument} className="font-medium text-sm" />
                        <span className="text-gray-600 text-xs">{formatTradeTime(t.open_time || t.created_at)}</span>
                      </div>
                      <span className={`font-mono text-sm font-bold ${pnlColorClass(pnlVal)}`}>
                        {pnlVal > 0 ? '+' : ''}${pnlVal.toFixed(2)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <div className="bg-gray-900 rounded-lg border border-gray-800 p-5">
            <SectionHeader
              title={<span className="inline-flex items-center gap-2"><Clock size={14} className="text-gray-500" /> Next Macro Window</span>}
            />
            {marketClosed ? (
              <p className="text-gray-400 text-sm">{getNextMacroWhenClosed(now)}</p>
            ) : (
              <p className="font-mono text-lg text-white">Next window in {macro.label}</p>
            )}
          </div>
          <div className="bg-gray-900 rounded-lg border border-gray-800 p-5">
            <SectionHeader
              title={<span className="inline-flex items-center gap-2"><Calendar size={14} className="text-gray-500" /> Current Session</span>}
            />
            <p className={`font-mono text-lg ${session.label === 'Market Closed' ? 'text-gray-500' : 'text-white'}`}>
              {session.label}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function DirectionBadge({ dir }: { dir?: string }) {
  if (!dir) return null;
  const cls = dir === 'long' ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300';
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>
      {dir.toUpperCase()}
    </span>
  );
}

function TradingControlsBar({ positionStatus }: { positionStatus: 'FLAT' | 'LONG' | 'SHORT' | 'MIXED' }) {
  const [killSwitch, setKillSwitch] = useState<boolean | null>(null);
  const [autoExecute, setAutoExecute] = useState<boolean | null>(null);
  const [ksBusy, setKsBusy] = useState(false);
  const [aeBusy, setAeBusy] = useState(false);
  const [flattenBusy, setFlattenBusy] = useState(false);
  const [flattenMsg, setFlattenMsg] = useState<string | null>(null);
  const [flattenErr, setFlattenErr] = useState<string | null>(null);

  const readKillSwitch = (data: any): boolean => !!(data?.kill_switch ?? data?.halted);
  const readAutoExecute = (data: any): boolean => !!data?.autoexecute;

  const fetchStatus = useCallback(async () => {
    try {
      const r = await getTradingStatus();
      setKillSwitch(readKillSwitch(r.data));
      setAutoExecute(readAutoExecute(r.data));
    } catch {}
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const onToggleKill = async () => {
    setKsBusy(true);
    try {
      const r = await postKillSwitch();
      setKillSwitch(readKillSwitch(r.data));
    } catch {} finally { setKsBusy(false); }
  };

  const onToggleAuto = async () => {
    setAeBusy(true);
    try {
      const r = await toggleAutoExecute();
      setAutoExecute(readAutoExecute(r.data));
    } catch {} finally { setAeBusy(false); }
  };

  const onFlatten = async () => {
    if (!window.confirm('Flatten ALL open positions at market? This is immediate and irreversible.')) return;
    setFlattenBusy(true);
    setFlattenMsg(null);
    setFlattenErr(null);
    try {
      await postFlatten();
      setFlattenMsg('All positions flattened.');
    } catch (e: any) {
      setFlattenErr(e?.response?.data?.detail || 'Flatten failed');
    } finally {
      setFlattenBusy(false);
    }
  };

  const posCls =
    positionStatus === 'LONG' ? 'text-green-400'
    : positionStatus === 'SHORT' ? 'text-red-400'
    : positionStatus === 'MIXED' ? 'text-yellow-400'
    : 'text-gray-500';

  return (
    <div className="bg-gray-900 border-b border-gray-800 -mx-4 px-4 py-2 mb-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
        <div className="flex items-center gap-2">
          <span className="text-gray-400 font-semibold uppercase tracking-wider">Auto-Execute</span>
          <button
            onClick={onToggleAuto}
            disabled={aeBusy || autoExecute === null}
            className={`px-2.5 py-1 rounded font-bold text-xs transition-colors disabled:opacity-50 ${
              autoExecute === true ? 'bg-blue-600 hover:bg-blue-500 text-white' :
              autoExecute === false ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' :
              'bg-gray-800 text-gray-500'
            }`}
            title="Toggle auto-execute on/off"
          >
            {aeBusy ? '...' : autoExecute === null ? '--' : autoExecute ? 'ON' : 'OFF'}
          </button>
        </div>

        <div className="hidden sm:block h-4 w-px bg-gray-800" />

        <div className="flex items-center gap-2">
          <span className="text-gray-400 font-semibold uppercase tracking-wider">Kill Switch</span>
          <button
            onClick={onToggleKill}
            disabled={ksBusy || killSwitch === null}
            className={`px-2.5 py-1 rounded font-bold text-xs transition-colors disabled:opacity-50 ${
              killSwitch === true ? 'bg-red-600 hover:bg-red-500 text-white' :
              killSwitch === false ? 'bg-green-700 hover:bg-green-600 text-green-100' :
              'bg-gray-800 text-gray-500'
            }`}
            title="Toggle kill switch (halts new orders)"
          >
            {ksBusy ? '...' : killSwitch === null ? '--' : killSwitch ? 'ARMED' : 'SAFE'}
          </button>
        </div>

        <div className="hidden sm:block h-4 w-px bg-gray-800" />

        <div className="flex items-center gap-1.5">
          <span className={posCls}>●</span>
          <span className={`font-mono font-bold ${posCls}`}>{positionStatus}</span>
        </div>

        <div className="ml-auto flex items-center gap-3">
          {flattenMsg && <span className="text-green-400 text-xs">{flattenMsg}</span>}
          {flattenErr && <span className="text-red-400 text-xs">{flattenErr}</span>}
          <button
            onClick={onFlatten}
            disabled={flattenBusy}
            className="bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5 rounded"
          >
            {flattenBusy ? 'Flattening...' : 'Flatten All'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DashboardLoading() {
  return (
    <div>
      <PageHeader title="Dashboard" description="Loading live status..." />
      <div className="flex flex-col items-center justify-center h-48 text-gray-500 gap-3">
        <Activity size={28} className="animate-pulse" />
        <span className="text-sm">Loading...</span>
      </div>
    </div>
  );
}
